import { z } from "zod";
import { FALLBACK_WEAR_COST_PER_KM_CHF } from "../../../constants/pricing";
import type { SwissReductionCard } from "../../../types/GlobalContext";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/** Zod enum mirroring {@link SwissReductionCard} with safe catch on corrupt storage. */
const SwissReductionCardSchema = z.enum( [ "none", "half-fare", "ga" ] ).
    catch( "none" as SwissReductionCard ).
    default( "none" as SwissReductionCard );

/**
 * Validates wear cost read from storage.
 *
 * @param value Wear cost candidate from storage.
 * @returns `true` when finite and non-negative.
 */
const isValidWearCostPerKm = ( value: number ): boolean => Number.isFinite( value ) && value >= 0;

/** Zod schema for pricing display and itinerary pricing inputs. */
const PricingPrefsSchema = z.object( {
    "displayCurrency":  z.enum( [ "CHF", "EUR" ] ).default( "CHF" ),
    "wearCostPerKmChf": z.number().
        refine( isValidWearCostPerKm ).
        catch( FALLBACK_WEAR_COST_PER_KM_CHF ).
        default( FALLBACK_WEAR_COST_PER_KM_CHF ),
    "swissReductionCard": SwissReductionCardSchema,
    "swissSuperSaver":    z.boolean().default( true ),
    "categoryOfService":  z.string().nullable().
        default( null ),
} );

/** In-memory and persisted pricing preferences (includes UI currency). */
export type PricingPrefs = z.infer<typeof PricingPrefsSchema>;

/** ISO currency code used for FAB toggle and offer requests. */
export type DisplayCurrency = PricingPrefs["displayCurrency"];

/** Parsed defaults before hydration. */
export const PRICING_PREFS_DEFAULT: PricingPrefs = PricingPrefsSchema.parse( {} );

/**
 * Maps persisted currency to the legacy boolean used in some UI paths.
 *
 * @param currency Stored display currency.
 * @returns `true` when currency is EUR.
 */
export const displayCurrencyToIsEuro = ( currency: DisplayCurrency ): boolean => currency === "EUR";

/**
 * Maps legacy boolean UI flag to ISO currency for persistence and API.
 *
 * @param isEuro When true, persists EUR; otherwise CHF.
 * @returns ISO currency code for `displayCurrency`.
 */
export const isEuroToDisplayCurrency = ( isEuro: boolean ): DisplayCurrency => ( isEuro ? "EUR" : "CHF" );

/** Persisted slice: currency, wear cost, Swiss card/saver, category of service. */
export const pricingPrefsSlice = definePersistedSlice<PricingPrefs>( {
    "key":            "pricingPrefs",
    "currentVersion": 1,
    "storage":        "preferences",
    "eager":          true,
    "schema":         PricingPrefsSchema,
    "default":        () => PRICING_PREFS_DEFAULT,
    "migrations":     {},
} );
